import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { myApplications, withdrawApplication } from "@/lib/jobsApi";
import ErrorState from "@/pages/jobs/ErrorState";

const STATUS_STYLE = {
  submitted: "bg-slate-100 text-slate-700",
  viewed: "bg-blue-50 text-blue-700",
  shortlisted: "bg-amber-50 text-amber-700",
  interviewing: "bg-indigo-50 text-indigo-700",
  hired: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  withdrawn: "bg-slate-50 text-slate-500",
};

export default function JobApplicationsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-applications"], queryFn: myApplications });
  const withdraw = useMutation({
    mutationFn: withdrawApplication,
    onSuccess: () => { toast.success("Application withdrawn."); qc.invalidateQueries({ queryKey: ["my-applications"] }); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Cannot withdraw"),
  });
  const apps = q.data?.applications || [];
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <h1 className="font-display text-3xl font-black text-slate-900" data-testid="my-applications-title">
        My applications
      </h1>
      <ul className="mt-6 space-y-3" data-testid="my-applications-list">
        {q.isLoading && <li className="text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</li>}
        {q.isError && <li><ErrorState onRetry={() => q.refetch()} /></li>}
        {!q.isLoading && !q.isError && !apps.length && <li className="text-slate-500 text-sm">No applications yet.</li>}
        {apps.map((a) => (
          <li key={a.id} data-testid={`application-${a.id}`} className="rounded-2xl bg-white border border-soft p-4 md:p-5 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <Link to={`/jobs/applications/${a.id}`} data-testid={`application-open-${a.id}`} className="font-display font-bold text-slate-900 hover:text-[color:var(--brand-blue)]">{a.job_title}</Link>
              <div className="text-xs text-slate-500 mt-0.5">
                {a.employer_name} · Applied {new Date(a.submitted_at).toLocaleDateString("en-NZ")}
              </div>
              <Link to={`/ad/${a.job_listing_id}`} className="text-xs text-slate-400 hover:text-[color:var(--brand-blue)]">View job listing →</Link>
            </div>
            <span data-testid={`app-status-${a.id}`} className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLE[a.status] || "bg-slate-100 text-slate-500"}`}>
              {a.status}
            </span>
            {!["withdrawn", "rejected", "hired"].includes(a.status) && (
              <button
                data-testid={`withdraw-btn-${a.id}`}
                onClick={() => withdraw.mutate(a.id)}
                className="text-xs font-semibold text-red-500 hover:underline"
              >
                Withdraw
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
